import { FC, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CodeCard,
  CodeCardProps,
  CodeRun,
  CodeRunDataSource,
} from "@quark/design";
import { checkCodeHasSdtin, execCode, ExecCodeResp } from "../../api";
import { useChatGpt } from "@quark/ai-assistant";

interface CodeProps extends CodeCardProps {
  compId: string;
  entryFilename: string;
  timeLimit: number;
  dataSource: CodeRunDataSource;
}

const Code: FC<CodeProps> = ({
  compId,
  entryFilename,
  timeLimit,
  code,
  language,
  runnable,
  dataSource,
}) => {
  const routeParams = useParams();
  const chatGpt = useChatGpt();
  const { courseId, pageId } = routeParams;
  const [showCodeRun, setShowCodeRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [dataResult, setDataResult] = useState<ExecCodeResp | undefined>(
    undefined
  );

  const checkSdtinFn = (lang: string, code: string) => {
    return checkCodeHasSdtin(lang, code)
      .then(({ has_stdin }) => {
        return has_stdin;
      })
      .catch(() => {
        return false;
      });
  };

  const runCode = (
    stdin: string,
    code: string | undefined,
    language: string
  ) => {
    if (!code) {
      return;
    }
    const params = {
      language,
      source_code: code || "",
      stdin,
      course_no: courseId || "",
      page_id: pageId || "",
      comp_id: compId,
      additional_files: "[]",
      entry_filename: entryFilename,
      time_limit: timeLimit,
    };
    setRunning(true);
    execCode(params)
      .then((data) => {
        setDataResult({
          ...data,
          stdin: params.stdin,
        });
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setRunning(false);
      });
  };

  const handleAIClick = () => {
    chatGpt.sendCodePrompt({
      language: language,
      sourceCode: code,
      mode: "explain",
      stdin: dataResult?.stdin || "",
      stdout: dataResult?.stdout || "",
      stderr: dataResult?.stderr || "",
    });
  };

  return (
    <>
      <CodeCard
        code={code}
        language={language}
        runnable={runnable}
        onButtonClick={() => {
          setShowCodeRun(true);
        }}
        onAIClick={handleAIClick}
      />
      <CodeRun
        visible={showCodeRun}
        running={running}
        dataSource={dataSource}
        dataResult={dataResult}
        checkSdtinFn={checkSdtinFn}
        onClose={() => setShowCodeRun(false)}
        onRun={runCode}
        onChatGPT={handleAIClick}
      />
    </>
  );
};

export default Code;
